import { GameEntity, GameStatus, Role } from '@okletsplay/core';
import { Route } from 'app/enums';
import plusIcon from 'assets/images/plus_icon.svg';
import { gamesSelector } from 'modules/games/store/games/selectors';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GameNavigationMenuItem } from 'app/interfaces/sidebar/game-navigation-menu-item';
import { useNavigate } from 'react-router-dom';
import { useRoles } from 'modules/auth/hooks';

interface GameMenuItemsHook {
  gameMenuItems: GameNavigationMenuItem[];
}

export function useGameMenuItems(): GameMenuItemsHook {
  const navigate = useNavigate();
  const allGames = useSelector(gamesSelector.all);

  const { hasAllRoles } = useRoles();

  const createGameMenuItem: GameNavigationMenuItem = useMemo(
    () => ({
      label: 'Add Game',
      icon: plusIcon,
      roles: [Role.User, Role.GameAdministrator],
      route: Route.Games,
      onClick: () => navigate(Route.Games),
      onEdit: () => null,
      editable: false,
    }),
    [navigate]
  );

  const gameMapper = useCallback(
    (game: GameEntity): GameNavigationMenuItem => ({
      label: game?.title,
      route: `${Route.Games}/${game?.id}`,
      onClick: () => navigate(`${Route.Games}/${game?.id}`),
      onEdit: () => navigate(`${Route.Games}/${game?.id}/edit`),
      roles: game?.status === GameStatus.Enabled ? [] : [Role.User, Role.GameAdministrator],
      disabled: game?.status === GameStatus.Disabled,
      isAllowedWithoutAuthorization: true,
      icon: game.icon,
      editable: true,
    }),
    [navigate]
  );

  const gameMenuItems = useMemo((): GameNavigationMenuItem[] => {
    const games = allGames.map(gameMapper);
    const menuItems = [...games, createGameMenuItem];

    return menuItems.filter((item) => hasAllRoles(item?.roles ?? []));
  }, [allGames, createGameMenuItem, gameMapper, hasAllRoles]);

  return { gameMenuItems };
}
